import { apiClient } from '@/lib/api/api-client'
import { createLogger } from '@/lib/logger'
import { createSelectors } from '@/store/factories/createSelectors'
import { useOrganizationStore } from '@/store/organization.store'
import { getCurrentUserId } from '@/utils/reaction-helpers'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import {
  type PersonalTaskBucket as TaskBucket,
  PreferenceContextEnum,
  TasksPersonalPreferences,
} from '@scrambl/shared'

import type {
  MyWorkAttentionBucket,
  MyWorkTab,
} from '@/pages/tasks/my-work/model'

const logger = createLogger('store/myTasks/store')

interface MyTasksState {
  preferences: TasksPersonalPreferences | null
  isLoading: boolean
  isSaving: boolean
  error: string | null
  /** Timestamp of last realtime update to detect stale local updates */
  lastRealtimeUpdate: number
  /**
   * Active All/Tasks/Tickets tab on the My Work page. Ephemeral (not
   * persisted) — lives here so the page header view toggle can react to it.
   */
  myWorkTypeFilter: MyWorkTab
  setMyWorkTypeFilter: (filter: MyWorkTab) => void
  /** Pin a work item (TASK:id / TICKET:id key) to a My Work bucket. */
  setMyWorkBucketOverride: (
    workKey: string,
    bucket: MyWorkAttentionBucket,
  ) => void
  loadPreferences: () => Promise<void>
  updatePreferences: (
    updates: Partial<TasksPersonalPreferences>,
  ) => Promise<void>
  /** Set preferences directly from realtime events (skips API save) */
  setPreferencesFromRealtime: (preferences: TasksPersonalPreferences) => void
  cleanupPreferences: (validTaskIds: string[]) => void
  getTaskBucket: (taskId: string) => TaskBucket | null
  moveTaskToBucket: (
    taskId: string,
    bucket: TaskBucket,
    newIndex?: number,
  ) => void
  reorderTaskInBucket: (
    taskId: string,
    bucket: TaskBucket,
    newIndex: number,
  ) => void
  clearBucketAssignment: (taskId: string) => void
  toggleBucketCollapse: (bucket: TaskBucket) => void
  isBucketCollapsed: (bucket: TaskBucket) => boolean
  updateFilters: (filters: {
    search?: string
    entityIds?: string[]
    projectIds?: string[]
    statuses?: string[]
    dueDateFrom?: string
    dueDateTo?: string
    scheduledForFrom?: string
    scheduledForTo?: string
  }) => void
}

const createDefaultPreferences = (): TasksPersonalPreferences => ({
  version: 1,
  order: {
    today: [],
    tomorrow: [],
    thisWeek: [],
    nextWeek: [],
    later: [],
    unscheduled: [],
  },
  lastTouched: new Date().toISOString(),
  viewMode: 'list',
  hideCompleted: true,
  groupByProject: false,
  collapsedBuckets: [],
  widget: {
    showCalendarEvents: true,
  },
  filters: {
    search: '',
    entityIds: [],
    projectIds: [],
    statuses: [],
  },
})

const areJsonEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

// Track pending save to handle rapid updates
let pendingSaveTimeout: ReturnType<typeof setTimeout> | null = null
let pendingPreferences: TasksPersonalPreferences | null = null
let activeLoadPreferencesPromise: Promise<void> | null = null
let loadedPreferencesKey: string | null = null

/**
 * Save preferences to backend with debouncing
 * Returns a promise that resolves when save completes (or rejects on failure)
 */
const savePreferencesToBackend = async (
  preferences: TasksPersonalPreferences,
  organizationId: string,
  userId: string,
): Promise<boolean> => {
  try {
    await apiClient('/user-preferences', {
      method: 'POST',
      body: {
        context: PreferenceContextEnum.TASKS_PERSONAL,
        preferences,
        organizationId,
        userId,
      },
    })
    return true
  } catch (error) {
    logger.error('[MyTasksStore] Failed to save preferences to backend:', error)
    return false
  }
}

/**
 * Debounced save that batches rapid updates
 * Stores the callback to notify when save completes
 */
const debouncedSave = (
  preferences: TasksPersonalPreferences,
  organizationId: string,
  userId: string,
  onComplete?: (success: boolean) => void,
) => {
  pendingPreferences = preferences

  if (pendingSaveTimeout) {
    clearTimeout(pendingSaveTimeout)
  }

  pendingSaveTimeout = setTimeout(async () => {
    const prefsToSave = pendingPreferences
    pendingPreferences = null
    pendingSaveTimeout = null

    if (prefsToSave) {
      const success = await savePreferencesToBackend(
        prefsToSave,
        organizationId,
        userId,
      )
      onComplete?.(success)
    }
  }, 500) // Reduced debounce for faster sync
}

const baseMyTasksStore = create<MyTasksState>()(
  immer((set, get) => ({
    preferences: null,
    isLoading: false,
    isSaving: false,
    error: null,
    lastRealtimeUpdate: 0,
    myWorkTypeFilter: 'all',

    setMyWorkTypeFilter: filter => {
      set(state => {
        state.myWorkTypeFilter = filter
      })
    },

    setMyWorkBucketOverride: (workKey, bucket) => {
      const overrides = {
        ...((get().preferences?.myWorkBucketOverrides as
          | Record<string, MyWorkAttentionBucket>
          | undefined) ?? {}),
      }
      overrides[workKey] = bucket
      void get().updatePreferences({ myWorkBucketOverrides: overrides })
    },

    loadPreferences: async () => {
      const userId = getCurrentUserId()
      const organizationId =
        useOrganizationStore.getState().currentOrganization?.id
      const loadKey = `${organizationId || 'none'}:${userId || 'none'}`

      if (!userId || !organizationId) {
        logger.warn('[MyTasksStore] Missing user or organization ID')
        return
      }

      if (get().isLoading && activeLoadPreferencesPromise) {
        return activeLoadPreferencesPromise
      }

      if (loadedPreferencesKey === loadKey && get().preferences) {
        return
      }

      set(state => {
        state.isLoading = true
        state.error = null
      })

      activeLoadPreferencesPromise = (async () => {
        try {
          const response = await apiClient<{
            data: { preferences: TasksPersonalPreferences }
          }>(
            `/user-preferences?context=${PreferenceContextEnum.TASKS_PERSONAL}`,
          )

          const preferences =
            response?.data?.preferences ||
            (response as unknown as { preferences?: TasksPersonalPreferences })
              ?.preferences

          if (preferences) {
            // Migrate: merge overdue bucket into today if it exists (from older preference versions)
            const order = preferences.order as Record<string, string[]>
            if (order.overdue && order.overdue.length > 0) {
              order.today = [...order.overdue, ...(order.today || [])]
              delete order.overdue
              // Remove 'overdue' from collapsedBuckets if present
              if (preferences.collapsedBuckets) {
                preferences.collapsedBuckets =
                  preferences.collapsedBuckets.filter(
                    (b: string) => b !== 'overdue',
                  ) as TaskBucket[]
              }
              // Save migrated preferences back
              debouncedSave(preferences, organizationId, userId)
            } else if (order.overdue) {
              // Empty overdue array — just delete the key
              delete order.overdue
            }

            loadedPreferencesKey = loadKey
            set(state => {
              state.preferences = preferences
              state.isLoading = false
            })
            return
          }
        } catch (error) {
          logger.warn('[MyTasksStore] Failed to load from backend:', error)
        }

        // If backend returns nothing, use defaults
        loadedPreferencesKey = loadKey
        set(state => {
          state.preferences = createDefaultPreferences()
          state.isLoading = false
        })
      })()

      try {
        await activeLoadPreferencesPromise
      } finally {
        activeLoadPreferencesPromise = null
      }
    },

    updatePreferences: async (updates: Partial<TasksPersonalPreferences>) => {
      const { preferences } = get()
      const userId = getCurrentUserId()
      const organizationId =
        useOrganizationStore.getState().currentOrganization?.id

      if (!userId || !organizationId) {
        logger.warn('[MyTasksStore] Missing user or organization ID')
        return
      }

      // Store previous state for rollback
      const previousPreferences = preferences

      const nextPreferences: TasksPersonalPreferences = {
        ...createDefaultPreferences(),
        ...preferences,
        ...updates,
        lastTouched: new Date().toISOString(),
      }

      if (
        preferences &&
        areJsonEqual(
          { ...preferences, lastTouched: undefined },
          { ...nextPreferences, lastTouched: undefined },
        )
      ) {
        return
      }

      // Optimistically update UI
      set(state => {
        state.preferences = nextPreferences
        state.isSaving = true
      })

      // Save to backend with debouncing
      debouncedSave(nextPreferences, organizationId, userId, success => {
        set(state => {
          state.isSaving = false
        })

        if (!success && previousPreferences) {
          // Revert on failure
          logger.warn('[MyTasksStore] Save failed, reverting to previous state')
          set(state => {
            state.preferences = previousPreferences
            state.error = 'Failed to save changes'
          })
        }
      })
    },

    setPreferencesFromRealtime: (preferences: TasksPersonalPreferences) => {
      const currentPrefs = get().preferences

      // Compare lastTouched to avoid applying stale updates
      if (currentPrefs?.lastTouched && preferences.lastTouched) {
        const incomingTime = new Date(preferences.lastTouched).getTime()
        const currentTime = new Date(currentPrefs.lastTouched).getTime()
        if (incomingTime <= currentTime) {
          logger.debug('[MyTasksStore] Skipping stale realtime update')
          return
        }
      }

      logger.info('[MyTasksStore] Applying preferences from realtime event')
      set(state => {
        state.preferences = preferences
        state.lastRealtimeUpdate = Date.now()
      })
    },

    cleanupPreferences: (_validTaskIds: string[]) => {
      // No cleanup needed without pinned functionality
    },

    getTaskBucket: (taskId: string) => {
      const { preferences } = get()
      if (!preferences) return null

      for (const [bucket, taskIds] of Object.entries(preferences.order)) {
        if (taskIds.includes(taskId)) {
          return bucket as TaskBucket
        }
      }

      return null
    },

    moveTaskToBucket: (
      taskId: string,
      bucket: TaskBucket,
      newIndex?: number,
    ) => {
      const { preferences, updatePreferences } = get()
      if (!preferences) {
        logger.warn('[MyTasksStore] No preferences loaded yet')
        return
      }

      const newOrder = { ...preferences.order }

      Object.keys(newOrder).forEach(b => {
        newOrder[b as TaskBucket] = newOrder[b as TaskBucket].filter(
          id => id !== taskId,
        )
      })

      if (!newOrder[bucket]) {
        newOrder[bucket] = []
      }
      const insertIndex =
        typeof newIndex === 'number'
          ? Math.max(0, Math.min(newIndex, newOrder[bucket].length))
          : newOrder[bucket].length
      newOrder[bucket].splice(insertIndex, 0, taskId)

      updatePreferences({
        order: newOrder,
      })
    },

    reorderTaskInBucket: (
      taskId: string,
      bucket: TaskBucket,
      newIndex: number,
    ) => {
      const { preferences, updatePreferences } = get()
      if (!preferences) return

      const bucketTasks = [...preferences.order[bucket]]
      const currentIndex = bucketTasks.indexOf(taskId)

      if (currentIndex === -1) {
        bucketTasks.splice(newIndex, 0, taskId)
      } else {
        bucketTasks.splice(currentIndex, 1)
        bucketTasks.splice(newIndex, 0, taskId)
      }

      updatePreferences({
        order: {
          ...preferences.order,
          [bucket]: bucketTasks,
        },
      })
    },

    clearBucketAssignment: (taskId: string) => {
      const { preferences, updatePreferences } = get()
      if (!preferences) return

      const newOrder = { ...preferences.order }

      Object.keys(newOrder).forEach(bucket => {
        newOrder[bucket as TaskBucket] = newOrder[bucket as TaskBucket].filter(
          id => id !== taskId,
        )
      })

      updatePreferences({
        order: newOrder,
      })
    },

    toggleBucketCollapse: (bucket: TaskBucket) => {
      const { preferences, updatePreferences } = get()

      const currentPrefs = preferences || createDefaultPreferences()
      const collapsedBuckets = currentPrefs.collapsedBuckets || []
      const isCollapsed = collapsedBuckets.includes(bucket)

      const newCollapsedBuckets = isCollapsed
        ? collapsedBuckets.filter(b => b !== bucket)
        : [...collapsedBuckets, bucket]

      if (!preferences) {
        // Initialize preferences first
        const userId = getCurrentUserId()
        const organizationId =
          useOrganizationStore.getState().currentOrganization?.id

        const newPrefs = {
          ...currentPrefs,
          collapsedBuckets: newCollapsedBuckets,
          lastTouched: new Date().toISOString(),
        }

        set(state => {
          state.preferences = newPrefs
        })

        if (userId && organizationId) {
          debouncedSave(newPrefs, organizationId, userId)
        }
        return
      }

      updatePreferences({
        collapsedBuckets: newCollapsedBuckets,
      })
    },

    isBucketCollapsed: (bucket: TaskBucket) => {
      const { preferences } = get()
      if (!preferences) return false
      return preferences.collapsedBuckets?.includes(bucket) || false
    },

    updateFilters: (filters: {
      search?: string
      entityIds?: string[]
      projectIds?: string[]
      statuses?: string[]
      dueDateFrom?: string
      dueDateTo?: string
      scheduledForFrom?: string
      scheduledForTo?: string
    }) => {
      const { preferences, updatePreferences } = get()
      if (!preferences) return

      const currentFilters = preferences.filters || {}
      const nextFilters = {
        ...currentFilters,
        ...filters,
      }
      if (areJsonEqual(currentFilters, nextFilters)) {
        return
      }

      updatePreferences({
        filters: nextFilters,
      })
    },
  })),
)

export const useMyTasksStore = createSelectors(baseMyTasksStore)
